import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useTranslation } from '@/hooks/useTranslation';
import type { CommonTranslationKey } from '@/i18n/types';
import { Check, Loader2, X } from 'lucide-react';
import React from 'react';

export interface StatusBadgeProps<T = unknown> {
  /** The entity object containing status information */
  entity: T & { is_active: boolean };
  /** Whether the user can edit the status */
  canEdit: boolean;
  /** Whether the popover is open */
  isPopoverOpen: boolean;
  /** Callback when popover open state changes */
  onPopoverChange: (open: boolean) => void;
  /** Callback when status should be changed */
  onStatusChange: (entity: T & { is_active: boolean }) => void;
  /** Whether the status update is in progress */
  isUpdating: boolean;
  /** Translation keys for status text */
  statusTranslationKeys: {
    active: CommonTranslationKey;
    inactive: CommonTranslationKey;
    confirmActivate: CommonTranslationKey;
    confirmDeactivate: CommonTranslationKey;
  };
  /** Optional custom class name */
  className?: string;
}

/**
 * StatusBadge - A reusable component for displaying and editing entity status
 *
 * This component provides a clickable badge that shows active/inactive status
 * and allows authorized users to toggle the status via a confirmation popover.
 *
 * @example
 * ```tsx
 * <StatusBadge
 *   entity={role}
 *   canEdit={hasPermission(user, 'roles', 'edit')}
 *   isPopoverOpen={popoverOpenRole === role.id}
 *   onPopoverChange={(open) => setPopoverOpenRole(open ? role.id : null)}
 *   onStatusChange={handleStatusChange}
 *   isUpdating={updateMutation.isPending}
 *   statusTranslationKeys={{
 *     active: 'roles.table.activeStatus.active',
 *     inactive: 'roles.table.activeStatus.inactive',
 *     confirmActivate: 'roles.messages.confirmActivate',
 *     confirmDeactivate: 'roles.messages.confirmDeactivate',
 *   }}
 * />
 * ```
 */
export const StatusBadge = <T,>({
  entity,
  canEdit,
  isPopoverOpen,
  onPopoverChange,
  onStatusChange,
  isUpdating,
  statusTranslationKeys,
  className,
}: StatusBadgeProps<T>): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <Popover open={isPopoverOpen} onOpenChange={onPopoverChange}>
      <PopoverTrigger asChild>
        <Badge
          variant={entity.is_active ? 'default' : 'secondary'}
          className={`${canEdit ? 'cursor-pointer hover:opacity-80' : ''} ${className || ''}`}
        >
          {entity.is_active
            ? t(statusTranslationKeys.active)
            : t(statusTranslationKeys.inactive)}
        </Badge>
      </PopoverTrigger>
      {canEdit && (
        <PopoverContent className='w-56 p-3'>
          <div className='flex flex-col space-y-3'>
            <p className='text-sm font-medium'>
              {entity.is_active
                ? t(statusTranslationKeys.confirmDeactivate)
                : t(statusTranslationKeys.confirmActivate)}
            </p>
            <div className='flex justify-end space-x-2'>
              <Button
                variant='outline'
                size='sm'
                onClick={() => onPopoverChange(false)}
                disabled={isUpdating}
                className='h-8 w-8 p-0'
                aria-label={t('common.actions.cancel')}
              >
                <X className='h-4 w-4' />
              </Button>
              <Button
                size='sm'
                onClick={() => onStatusChange(entity)}
                disabled={isUpdating}
                className='h-8 w-8 p-0'
                aria-label={t('common.actions.confirm')}
              >
                {isUpdating ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <Check className='h-4 w-4' />
                )}
              </Button>
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};

StatusBadge.displayName = 'StatusBadge';
